import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

export default function NewsDetail() {
  const { slug } = useParams();
  const [newsItem, setNewsItem] = useState(null);
  const [allNews, setAllNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetch('/_api/public/content/nyheder')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente nyhed');
        return res.json();
      })
      .then((items) => {
        if (Array.isArray(items)) {
          const mappedItems = items.map((n) => ({
            ...n,
            title: n.titel || n.title,
            summary: n.resume || n.summary,
            content: n.indhold || n.content,
            image: n.billede || n.image,
            date: n.dato || n.date
          }));
          setAllNews(mappedItems);
          const match = mappedItems.find(
            (n) => n.url_slug === slug || (n.slug && n.slug._ === slug)
          );
          if (match) {
            setNewsItem(match);
          } else {
            setError('Nyheden blev ikke fundet.');
          }
        }
      })
      .catch(() => {
        setError('Der opstod en fejl under indlæsning af nyheden.');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#f8faf5]">
        <div className="text-center space-y-3">
          <i className="fa-solid fa-spinner fa-spin text-3xl text-[#264A35]" aria-hidden="true" />
          <p className="text-sm font-medium text-[#5A6961]">Indlæser nyhed...</p>
        </div>
      </div>
    );
  }

  if (error || !newsItem) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#f8faf5] px-4 py-16">
        <div className="max-w-md text-center bg-[#eef4e6] p-8 rounded-3xl border border-[#d9e5d4] space-y-4">
          <i className="fa-solid fa-newspaper text-4xl text-[#C85A32]" aria-hidden="true" />
          <h1 className="text-2xl font-bold text-[#1D2923] font-serif-heading">
            Nyheden blev ikke fundet
          </h1>
          <Link
            to="/nyheder"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-white bg-[#264A35] hover:bg-[#1A3626]"
          >
            <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
            <span>Tilbage til nyheder</span>
          </Link>
        </div>
      </div>
    );
  }

  const otherNews = allNews
    .filter((n) => n.url_slug !== slug && (n.slug ? n.slug._ !== slug : true))
    .slice(0, 3);

  return (
    <article className="bg-white pb-20">
      
      {/* Article Header following container line */}
      <section className="bg-[#eef4e6] border-b border-[#d9e5d4] py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <Link
              to="/nyheder"
              className="inline-flex items-center gap-2 text-xs font-bold text-[#264A35] hover:text-black uppercase tracking-wider transition-colors"
            >
              <i className="fa-solid fa-arrow-left text-[10px]" aria-hidden="true" />
              <span>Tilbage til alle nyheder</span>
            </Link>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1D2923] tracking-tight font-serif-heading">
              {newsItem.title}
            </h1>

            {newsItem.summary && (
              <p className="text-base sm:text-lg text-[#5A6961] leading-relaxed pt-1">
                {newsItem.summary}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Article Body following container line */}
      <section className="py-12 lg:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          
          {/* Main Article Content */}
          <div className="lg:col-span-8 space-y-8">
            {newsItem.image && newsItem.image[0] && (
              <div className="rounded-3xl overflow-hidden shadow-xs border border-[#d9e5d4] bg-[#eef4e6]">
                <img
                  src={newsItem.image[0]}
                  alt={newsItem.title}
                  className="w-full h-auto max-h-[480px] object-cover"
                />
              </div>
            )}

            {/* Styled Rich Text */}
            <div
              className="text-[#3E4E45] text-base sm:text-lg leading-relaxed
                [&>p]:mb-6 [&>p]:leading-relaxed [&>p]:text-[#3E4E45]
                [&>h2]:text-2xl [&>h2]:sm:text-3xl [&>h2]:font-bold [&>h2]:text-[#1D2923] [&>h2]:font-serif-heading [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:tracking-tight
                [&>h3]:text-xl [&>h3]:sm:text-2xl [&>h3]:font-bold [&>h3]:text-[#1D2923] [&>h3]:font-serif-heading [&>h3]:mt-8 [&>h3]:mb-3
                [&>ul]:space-y-2.5 [&>ul]:my-6 [&>ul]:pl-6 [&>ul]:list-disc [&>ul]:marker:text-[#264A35]
                [&>ol]:space-y-2.5 [&>ol]:my-6 [&>ol]:pl-6 [&>ol]:list-decimal [&>ol]:marker:text-[#264A35]
                [&>blockquote]:border-l-4 [&>blockquote]:border-[#264A35] [&>blockquote]:bg-[#eef4e6] [&>blockquote]:p-5 [&>blockquote]:rounded-r-2xl [&>blockquote]:my-6 [&>blockquote]:text-[#1D2923] [&>blockquote]:italic
                [&>strong]:text-[#1D2923] [&>strong]:font-bold
                [&>a]:text-[#264A35] [&>a]:font-bold [&>a]:underline hover:[&>a]:text-black"
              dangerouslySetInnerHTML={{ __html: newsItem.content }}
            />

            <div className="pt-8 border-t border-[#d9e5d4] flex items-center justify-between">
              <Link
                to="/nyheder"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-[#264A35] bg-white hover:bg-[#eef4e6] border border-[#d9e5d4] transition-all"
              >
                <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                <span>Flere nyheder</span>
              </Link>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-28">
            {otherNews.length > 0 && (
              <div className="bg-white rounded-3xl p-6 border border-[#d9e5d4] shadow-xs space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#1D2923]">
                  Seneste nyheder
                </h3>
                <div className="space-y-4">
                  {otherNews.map((item) => (
                    <Link
                      key={item.url_slug}
                      to={`/nyheder/${item.url_slug}`}
                      className="block group p-3 rounded-2xl hover:bg-[#eef4e6] transition-all"
                    >
                      <div className="text-xs text-[#88A68E] font-semibold">{item.date}</div>
                      <h4 className="text-sm font-bold text-[#1D2923] group-hover:text-[#264A35] transition-colors mt-0.5 line-clamp-2">
                        {item.title}
                      </h4>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* CTA in sidebar */}
            <div className="bg-[#eef4e6] rounded-3xl p-6 border border-[#d9e5d4] text-center space-y-3">
              <h3 className="text-base font-bold text-[#1D2923] font-serif-heading">
                Oplev skolen i virkeligheden
              </h3>
              <p className="text-xs text-[#5A6961]">
                Book et uforpligtende besøg og mærk den gode stemning.
              </p>
              <Link
                to="/besoeg-skolen"
                className="inline-block w-full py-2.5 rounded-full text-xs font-bold text-white bg-[#264A35] hover:bg-[#1D2923] transition-all shadow-xs"
              >
                Besøg skolen
              </Link>
            </div>
          </aside>

        </div>
      </section>

    </article>
  );
}
